<script lang="ts">
	import { getContext, onMount } from "svelte";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import type { Poll } from "#gen/forms/v1/poll_pb.js";
	import type { Page } from "#gen/pages/v2/page_pb.js";
	import Label from "#lib/components/Label.svelte";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import AppearanceItem from "#lib/components/flow/editor/appearance/AppearanceItem.svelte";
	import TextAppearance from "#lib/components/page/components/poll/appearance/TextAppearance.svelte";
	import { PollState } from "#lib/components/page/components/poll/stateMachine.js";
	import { getDefaults, getTheme, setThemeValue } from "#lib/components/page/components/poll/theme.js";
	import { setEditorState } from "#lib/components/page/components/poll/utils.js";
	import { setStructValue, structValue, unsetValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { JsonObject, JsonValue } from "@bufbuild/protobuf";
	import type { Writable } from "svelte/store";

	interface Props {
		id: string;
		page: Writable<Page>;
		poll: Poll;
		onChange?: (poll: Poll) => void;
		onInput?: (poll: Poll) => void;
	}

	let { id, page, poll, onChange, onInput }: Props = $props();

	const editorState: Writable<Record<string, unknown>> = getContext("editorState");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");
	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");

	const config = $derived(getConfig($page, id));
	const themeDefaults = $derived(getDefaults($brandKit, $page));
	const theme = $derived(getTheme($brandKit, $page, config));

	onMount(() => {
		$editorState = setEditorState($editorState, poll, "overridePollState", PollState.Active);
		$editorState = setEditorState($editorState, poll, "overrideSelection", null);
	});

	function getConfig(page: Page, id: string): JsonObject {
		let config = structValue(page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue(page.config, id, config);
		}
		return config;
	}

	function handleChange(key: string, value: JsonValue) {
		setThemeValue($brandKit, $page, config, key, value);
		onChange?.(poll);
	}

	function handleInput(key: string, value: JsonValue) {
		setThemeValue($brandKit, $page, config, key, value);
		onInput?.(poll);
	}

	function resetToPageStyle(key: string) {
		const config = structValue($page.config, id);
		if (config) {
			unsetValue(config, key);
			onChange?.(poll);
		}
	}
</script>

<div class="flex w-full flex-col gap-6">
	<AppearanceItem title="Title">
		{#snippet content()}
			<Label for="pollHeaderText" class="font-medium">Text value</Label>
			<PlaintextEditor id="pollHeaderText" provider={$tokenProvider} bind:value={poll.heading} placeholder="Header text" required={true} on:input={() => onInput?.(poll)} on:change={() => onChange?.(poll)} />
		{/snippet}
		<TextAppearance prefix="header" values={theme} defaults={themeDefaults} onInput={handleInput} onChange={handleChange} onReset={resetToPageStyle} />
	</AppearanceItem>
</div>
